import React, { useState, useCallback } from 'react';
import { fetchCaptureImageBlob } from '../utils/storage';
import LazyBlobImage from './LazyBlobImage';
import './CapturesGallery.css';

/**
 * CaptureCard — displays a single capture image with its metadata.
 * Image bytes are fetched lazily (only when near viewport) and dropped
 * when the card scrolls out — see LazyBlobImage.
 */
function CaptureCard({ capture }) {
  const fetchBlob = useCallback(() => fetchCaptureImageBlob(capture.id), [capture.id]);

  return (
    <div className="cg-card">
      <div className="cg-image-wrapper">
        {capture.has_image ? (
          <LazyBlobImage
            fetchBlob={fetchBlob}
            alt={capture.description || capture.item_name}
            className="cg-image"
          />
        ) : (
          <div className="cg-image-placeholder cg-image-missing">No image</div>
        )}
        {/* Status tag (top-left). Two cases, mutually exclusive:
              • Hidden (orange)        — user intentionally excluded from the
                                         MS Word report (is_in_ms_word_report=false).
              • Not available (red)    — included in the report but the sync
                                         could not find the image file on
                                         drive (has_image=false). Excluded
                                         from the generated PDF as if hidden,
                                         but flagged distinctly so the user
                                         knows something is missing. */}
        {!capture.is_in_ms_word_report ? (
          <span className="cg-hidden-label">Hidden</span>
        ) : !capture.has_image ? (
          <span className="cg-hidden-label cg-not-available-label">Not available</span>
        ) : null}
      </div>
      <div className="cg-caption">
        <span className="cg-element-name">{capture.element_name}</span>
        {capture.unit_name && <span className="cg-unit-name">{capture.unit_name}</span>}
        {capture.description && <span className="cg-description">{capture.description}</span>}
      </div>
    </div>
  );
}

/** Chevron SVG — reused for both element and unit headings */
function CollapseChevron({ isOpen }) {
  return (
    <svg
      className={`cg-collapse-chevron ${isOpen ? 'open' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * CapturesGallery — groups captures by elementName (A-Z), then by unitName.
 *
 * Structure:
 *   Element Heading (A-Z) — collapsible, with image count
 *     Direct images (unitName === null), sorted by sort_order
 *     Unit Subheading (A-Z) — collapsible, with image count
 *       Unit images, sorted by sort_order
 */
export default function CapturesGallery({ captures }) {
  const [collapsedElements, setCollapsedElements] = useState(new Set());
  const [collapsedUnits, setCollapsedUnits] = useState(new Set());

  if (!captures || captures.length === 0) return null;

  function toggleElement(name) {
    setCollapsedElements((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleUnit(key) {
    setCollapsedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // ── Group captures by elementName ─────────────────────────────────
  const groupMap = new Map();
  for (const cap of captures) {
    const key = cap.element_name || '(Unknown)';
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key).push(cap);
  }

  // Sort element groups A→Z
  const sortedGroups = [...groupMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div className="cg-container">
      {sortedGroups.map(([elementName, groupCaptures]) => {
        const elementOpen = !collapsedElements.has(elementName);

        // Split into direct (no unit) and unit sub-groups
        const direct = groupCaptures
          .filter((c) => !c.unit_name)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

        // Group by unitName
        const unitMap = new Map();
        for (const c of groupCaptures) {
          if (!c.unit_name) continue;
          if (!unitMap.has(c.unit_name)) unitMap.set(c.unit_name, []);
          unitMap.get(c.unit_name).push(c);
        }
        const sortedUnits = [...unitMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));

        return (
          <div key={elementName} className="cg-element-group">
            <h3
              className="cg-element-heading"
              onClick={() => toggleElement(elementName)}
            >
              <span>
                {elementName}
                <span className="cg-heading-count"> ({groupCaptures.length})</span>
              </span>
              <CollapseChevron isOpen={elementOpen} />
            </h3>

            {elementOpen && (
              <>
                {/* Direct images (no unit) */}
                {direct.length > 0 && (
                  <div className="cg-grid">
                    {direct.map((cap) => (
                      <CaptureCard key={cap.id} capture={cap} />
                    ))}
                  </div>
                )}

                {/* Unit sub-groups */}
                {sortedUnits.map(([unitName, unitCaptures]) => {
                  const unitKey = `${elementName}|${unitName}`;
                  const unitOpen = !collapsedUnits.has(unitKey);
                  const sorted = unitCaptures.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

                  return (
                    <div key={unitName} className="cg-unit-group">
                      <h4
                        className="cg-unit-heading"
                        onClick={() => toggleUnit(unitKey)}
                      >
                        <span>
                          {unitName}
                          <span className="cg-heading-count"> ({unitCaptures.length})</span>
                        </span>
                        <CollapseChevron isOpen={unitOpen} />
                      </h4>
                      {unitOpen && (
                        <div className="cg-grid">
                          {sorted.map((cap) => (
                            <CaptureCard key={cap.id} capture={cap} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
